import { useCallback, useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

type UseElementWidthResult = {
	width: number;
	/** Callback ref to attach to the element being measured. */
	setNode: (node: HTMLDivElement | null) => void;
	/** Read-only ref to the most recently attached node, for non-render reads. */
	nodeRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Tracks the rendered width of a DOM element via ResizeObserver.
 * Pass `setNode` as the element's callback ref; `width` stays in sync.
 * Bails out of setState when the value hasn't changed to avoid wasted renders.
 */
export const useElementWidth = (): UseElementWidthResult => {
	const [width, setWidth] = useState(0);
	const nodeRef = useRef<HTMLDivElement | null>(null);
	const lastWidthRef = useRef(0);

	const commitWidth = useCallback((nextWidth: number) => {
		if (nextWidth <= 0 || nextWidth === lastWidthRef.current) {
			return;
		}
		lastWidthRef.current = nextWidth;
		setWidth(nextWidth);
	}, []);

	const setNode = useCallback(
		(node: HTMLDivElement | null) => {
			nodeRef.current = node;
			if (node) {
				commitWidth(node.getBoundingClientRect().width);
			}
		},
		[commitWidth],
	);

	useEffect(() => {
		const el = nodeRef.current;
		if (!el) {
			return;
		}
		const observer = new ResizeObserver((entries) => {
			for (const entry of entries) {
				commitWidth(entry.contentRect.width);
			}
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [commitWidth]);

	return { width, setNode, nodeRef };
};

type UseScrollStateResult = {
	/** True when the page has been scrolled past the top. */
	isScrolled: boolean;
};

/**
 * Tracks whether the page has been scrolled away from the top, used to drive
 * the sticky header's bottom-border keyline.
 *
 * Listens for scroll events on both the document (capture, to catch any
 * scrollable ancestor / sibling) and the window (for document-level scrolling
 * layouts).
 */
export const useScrollState = (): UseScrollStateResult => {
	const [isScrolled, setIsScrolled] = useState(false);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return;
		}

		const handleScroll = () => {
			// Only track the page-level scroll position. We deliberately ignore
			// nested scrollable elements (dropdowns, filter panels, etc.) by always
			// reading from the document root rather than event.target, which could
			// be any scrollable child element.
			setIsScrolled(window.scrollY > 0 || document.documentElement.scrollTop > 0);
		};

		handleScroll();

		// Use capture on document to catch scroll events from all sources.
		// Also bind window directly for document-level scrolling layouts.
		const unbindDocument = bind(document, {
			type: 'scroll',
			listener: handleScroll,
			options: { capture: true, passive: true },
		});
		const unbindWindow = bind(window, {
			type: 'scroll',
			listener: handleScroll,
			options: { passive: true },
		});
		return () => {
			unbindDocument();
			unbindWindow();
		};
	}, []);

	return { isScrolled };
};
